import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Settings2, CreditCard, Crown, Gem, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

const LEVELS = ['standard', 'gold', 'platinum', 'black'];
const LEVEL_ICON = {
  standard: CreditCard, gold: Sparkles, platinum: Crown, black: Gem,
};
const LEVEL_STYLE = {
  standard: 'bg-slate-200 text-slate-800',
  gold: 'bg-gradient-to-r from-yellow-300 to-amber-500 text-amber-900',
  platinum: 'bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900',
  black: 'bg-gradient-to-r from-slate-800 to-black text-amber-300',
};

function LevelBadge({ level }) {
  const Icon = LEVEL_ICON[level] || CreditCard;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${LEVEL_STYLE[level] || LEVEL_STYLE.standard}`}>
      <Icon className="w-3 h-3"/> {level}
    </span>
  );
}

export default function AdminCards() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState(null);
  const [editing, setEditing] = useState(null); // card object
  const [form, setForm] = useState({ card_level: 'standard', payment_limit_daily: '', withdrawal_limit_daily: '', payment_limit_monthly: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/cards');
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
    try {
      const lvl = await api.get('/admin/card-levels');
      setDefaults(lvl.data?.defaults || null);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (card) => {
    const lvl = card.card_level || card.niveau_carte || 'standard';
    setForm({
      card_level: LEVELS.includes(lvl) ? lvl : 'standard',
      payment_limit_daily: card.payment_limit_daily ?? '',
      withdrawal_limit_daily: card.withdrawal_limit_daily ?? '',
      payment_limit_monthly: card.payment_limit_monthly ?? '',
    });
    setEditing(card);
  };

  const applyLevelDefaults = (level) => {
    setForm((f) => ({ ...f, card_level: level }));
    if (defaults && defaults[level]) {
      const d = defaults[level];
      setForm((f) => ({
        ...f,
        card_level: level,
        payment_limit_daily: d.payment_limit_daily,
        withdrawal_limit_daily: d.withdrawal_limit_daily,
        payment_limit_monthly: d.payment_limit_monthly,
      }));
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        card_level: form.card_level,
        payment_limit_daily: form.payment_limit_daily === '' ? null : Number(form.payment_limit_daily),
        withdrawal_limit_daily: form.withdrawal_limit_daily === '' ? null : Number(form.withdrawal_limit_daily),
        payment_limit_monthly: form.payment_limit_monthly === '' ? null : Number(form.payment_limit_monthly),
      };
      // Prefer dedicated endpoint
      await api.put(`/admin/clients/${editing.user_id}/card/limits`, payload).catch(async () => {
        // Legacy fallback
        await api.put(`/admin/cards/${editing.id}/level`, { level: payload.card_level, custom_limits: payload });
      });
      toast.success('Carte mise à jour');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">{t('admin.cardsTitle')}</h1>
        {defaults && (
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>Niveaux :</span>
            {LEVELS.map((l) => <LevelBadge key={l} level={l}/>)}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left p-3">Titulaire</th>
                <th className="p-3 text-left">Numéro</th>
                <th className="p-3">Type</th>
                <th className="p-3">Niveau</th>
                <th className="p-3">Plafonds (paiement / retrait / mensuel)</th>
                <th className="p-3">Expire</th>
                <th className="p-3">Statut</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></td></tr>}
              {!loading && items.map((c) => {
                const lvl = c.card_level || c.niveau_carte || 'standard';
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="p-3">{c.titulaire}</td>
                    <td className="p-3 font-mono">**** **** **** {c.numero_carte?.slice(-4)}</td>
                    <td className="p-3 text-center capitalize">{c.type_carte}</td>
                    <td className="p-3 text-center"><LevelBadge level={lvl}/></td>
                    <td className="p-3 text-center font-mono text-xs">
                      {fmtLimit(c.payment_limit_daily)} / {fmtLimit(c.withdrawal_limit_daily)} / {fmtLimit(c.payment_limit_monthly)}
                    </td>
                    <td className="p-3 text-center font-mono">{c.date_expiration}</td>
                    <td className="p-3 text-center"><Badge variant={c.statut === 'active' ? 'default' : 'destructive'}>{c.statut}</Badge></td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`btn-edit-card-${c.id}`}>
                        <Settings2 className="w-3.5 h-3.5 mr-1"/> Gérer
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Aucune carte générée</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gérer la carte — {editing?.titulaire}</DialogTitle>
            <DialogDescription>Modifier le niveau de carte et les plafonds. Laissez 0 pour illimité.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Niveau de carte</Label>
              <Select value={form.card_level} onValueChange={applyLevelDefaults}>
                <SelectTrigger data-testid="select-card-level"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => <SelectItem key={l} value={l}><span className="capitalize">{l}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Paiement / jour (€)</Label><Input type="number" value={form.payment_limit_daily} onChange={(e) => setForm({ ...form, payment_limit_daily: e.target.value })} data-testid="input-pmt-daily"/></div>
              <div><Label>Retrait / jour (€)</Label><Input type="number" value={form.withdrawal_limit_daily} onChange={(e) => setForm({ ...form, withdrawal_limit_daily: e.target.value })} data-testid="input-wd-daily"/></div>
              <div><Label>Paiement / mois (€)</Label><Input type="number" value={form.payment_limit_monthly} onChange={(e) => setForm({ ...form, payment_limit_monthly: e.target.value })} data-testid="input-pmt-monthly"/></div>
            </div>
            <div className="text-xs text-slate-500">Changer le niveau réinitialise les plafonds aux valeurs par défaut. Vous pouvez ensuite les personnaliser.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700" data-testid="btn-save-card-level">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Settings2 className="w-4 h-4 mr-1"/>} {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtLimit(v) {
  if (v == null) return '—';
  if (v <= 0 || v >= 999_999_999) return '∞';
  return `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€`;
}
